import { File, UploadType } from 'expo-file-system';
import { Platform } from 'react-native';
import { IMAGE_UPLOAD_TIMEOUT } from '@/utils/constants';

function getExtension(uri: string | null | undefined) {
  if (!uri) {
    return null;
  }
  const match = /\.([a-zA-Z0-9]+)(?:\?|#|$)/.exec(uri);
  if (match?.[1]) {
    return match[1].toLowerCase();
  }
  return null;
}
function inferMimeType({
  uri,
  providedMimeType,
}: {
  uri?: string | null;
  providedMimeType?: string | null;
}) {
  if (providedMimeType) {
    return providedMimeType;
  }
  const extension = getExtension(uri || '') || '';
  switch (extension) {
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'heic':
    case 'heif':
      return 'image/heic';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    default:
      return 'image/jpeg';
  }
}

function getImageIdFromUploadResponse(json: unknown) {
  if (!json || typeof json !== 'object') {
    return null;
  }

  const result = Reflect.get(json, 'result');
  if (result && typeof result === 'object') {
    const resultId = Reflect.get(result, 'id');
    if (typeof resultId === 'string') {
      return resultId;
    }
  }

  const id = Reflect.get(json, 'id');
  return typeof id === 'string' ? id : null;
}

async function uploadNativeImage({
  fileUri,
  fileMimeType,
  uploadUrl,
  signal,
}: {
  fileUri: string;
  fileMimeType: string;
  uploadUrl: string;
  signal: AbortSignal;
}): Promise<unknown> {
  const file = new File(fileUri);
  const response = await file.upload(uploadUrl, {
    httpMethod: 'POST',
    uploadType: UploadType.MULTIPART,
    fieldName: 'file',
    mimeType: fileMimeType,
    signal,
  });

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Upload failed with status ${response.status}.`);
  }

  const json: unknown = JSON.parse(response.body);
  return json;
}

async function uploadWebImage({
  path,
  url,
  uploadUrl,
  signal,
}: {
  path: string;
  url: string;
  uploadUrl: string;
  signal: AbortSignal;
}): Promise<unknown> {
  const formData = new FormData();
  const response = await fetch(url);
  const blob = await response.blob();
  formData.append('file', blob, `${process.env.EXPO_PUBLIC_ENV_NAME}/${path}`);

  const uploadResponse = await fetch(uploadUrl, {
    signal,
    method: 'POST',
    body: formData,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed with status ${uploadResponse.status}.`);
  }

  const json: unknown = await uploadResponse.json();
  return json;
}

export default function useUploadImage() {
  return async (
    path: string,
    url: string,
    uploadUrl: string,
    mimeType?: string | null,
  ): Promise<string | null> => {
    if (!uploadUrl) {
      return null;
    }

    const fileMimeType = inferMimeType({
      uri: url,
      providedMimeType: mimeType,
    });
    const abortController = new AbortController();
    const timeout = setTimeout(() => {
      abortController.abort();
    }, IMAGE_UPLOAD_TIMEOUT);

    try {
      const json =
        Platform.OS === 'web'
          ? await uploadWebImage({
              path,
              url,
              uploadUrl,
              signal: abortController.signal,
            })
          : await uploadNativeImage({
              fileUri: url,
              fileMimeType,
              uploadUrl,
              signal: abortController.signal,
            });
      const imageId = getImageIdFromUploadResponse(json);
      if (!imageId) {
        throw new Error('Upload did not return an image id.');
      }
      return imageId;
    } finally {
      clearTimeout(timeout);
    }
  };
}
